import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { RoundListNavItem, SeasonNavItem } from './nav-items';
import { RoundByRoundCards } from './round-by-round-cards';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { getTranslation } from 'common/react/utils/translations';
import {
    changeRound,
    initialiseNav
} from 'common/store/modules/competition-nav/thunks';
import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { useSyncUrlParams } from 'common/react/hooks/use-sync-url-params';
import { useGetUrlParams } from 'common/react/components/competition-nav/hooks/use-get-url-params';
import { useHasChangedFromDefaults } from 'common/react/components/competition-nav/hooks/use-has-changed-from-defaults';
import { useResetToDefaults } from 'common/react/components/competition-nav/hooks/use-reset-to-defaults';
import { useGetCurrentRoundNumber } from './hooks/use-find-current-round';

const NavWrapper = ({ children }) => (
    <div className="competition-nav filter-dropdown round-by-round">
        {children}
    </div>
);
NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const RoundByRound = ({
    seasonsToShow,
    seasonPid,
    seasonId,
    competitionId,
    shouldHidePredicted,
    title,
    startDateTime
}) => {
    const nav = useSelector(navSelector);
    const currentRoundNumber = useGetCurrentRoundNumber(nav.season.providerId);

    const dispatch = useDispatch();
    const roundByRoundRef = useRef();
    const windowSize = useWindowSizeV2();

    const options = useCompetitionNavigationOptions(nav);

    options.defaultSeason = nav.season.providerId ?? seasonPid;
    options.seasonsToShow = seasonsToShow;

    const params = useGetUrlParams();

    useEffect(() => {
        dispatch(
            initialiseNav({
                initialValues: {
                    competitionId: competitionId,
                    seasonId: params.season || seasonId
                },
                skipRound: true,
                removeFinals: true
            })
        );
    }, [dispatch]);

    useEffect(() => {
        if (nav.rounds.length > 0 && !nav.ready && currentRoundNumber > -1) {
            let round = nav.rounds.find(
                (item) => item.roundNumber === currentRoundNumber
            );

            if (!round) {
                const roundId = params.round || nav.round.id;
                round = nav.rounds.find((item) => item.id === roundId);
            }

            dispatch(
                changeRound({
                    round: round
                })
            );
        }
    }, [currentRoundNumber, nav.ready, nav.rounds]);

    useSyncUrlParams({
        selector: navSelector,
        mapToParams: (items) => ({
            Season: items.season.id,
            Round: items.round.id
        })
    });

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            season: nav.season,
            round: { roundNumber: nav.round }
        },
        options,
        filtersFields: ['season', 'round'],
        filtersRef: roundByRoundRef
    });

    const { hasChangedFromDefaults } = useHasChangedFromDefaults({
        state: { nav: nav },
        initialValues: {
            seasonId
        }
    });

    const resetToDefaults = useResetToDefaults({
        initialValues: {
            competitionId,
            seasonId
        }
    });

    return (
        <div ref={roundByRoundRef}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <SeasonNavItem
                            classes="season-nav-item"
                            seasonsToShow={seasonsToShow}
                            title={title}
                            isReadyToRender={windowSize.width !== null}
                        />
                        {hasChangedFromDefaults && !showResponsiveItems ? (
                            <div className=" stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                        {showResponsiveItems ? (
                            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                                <SettingsButton />
                            </StatsSection.SettingsButton>
                        ) : null}
                    </StatsSection.NavItems>
                    {hasChangedFromDefaults && showResponsiveItems ? (
                        <StatsSection.NavFilterItems hideReset>
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        </StatsSection.NavFilterItems>
                    ) : null}
                    <StatsSection.SettingsButton>
                        <SettingsButton />
                    </StatsSection.SettingsButton>
                    <StatsSection.RoundButtons>
                        <RoundListNavItem
                            classes={'competition-nav__round-filter-wrapper'}
                            title={title}
                        />
                    </StatsSection.RoundButtons>
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentBody>
                        <RoundByRoundCards
                            shouldHidePredicted={shouldHidePredicted === 'true'}
                            startDateTime={startDateTime}
                        />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

RoundByRound.propTypes = {
    seasonsToShow: PropTypes.string,
    seasonPid: PropTypes.string,
    seasonId: PropTypes.string,
    competitionId: PropTypes.string,
    shouldHidePredicted: PropTypes.string,
    title: PropTypes.string,
    startDateTime: PropTypes.string
};
